import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { ConfigurationSettings } from 'models/configuration-settings';
import { getMemberSubscriptions, moveOntoSeats, requestSeatSync } from '../services/seat-service';
import { readPendingSeats, writePendingSeats } from '../utils/pending-seats';
import { SUBSCRIPTION_NOT_LIVE, utilitiesErrorCode } from '../utils/seat-pools';

const RETRY_MS = 5000;
const GIVE_UP_AFTER_MS = 3 * 60 * 1000;

/**
 * Gives every remembered seat whose subscription has gone live. A subscription still waiting for its
 * payment keeps its entry for the next try; any other answer settles it. Returns how many still wait.
 */
export const settlePendingSeats = async (config: ConfigurationSettings): Promise<number> => {
  const pending = readPendingSeats();
  if (pending.length === 0) return 0;
  // A cancelled subscription also answers "not live", forever, so drop entries whose subscription is
  // gone from the list. If the list cannot be read, try every entry as before.
  const listed = await getMemberSubscriptions(config)
    .then((subscriptions) => new Set(subscriptions.map((subscription) => subscription.subscriptionId)))
    .catch(() => null);
  const waiting = [];
  for (const item of pending) {
    if (listed && !listed.has(item.subscriptionId)) continue;
    try {
      await moveOntoSeats(item.subscriptionId, item.userIds, item.releases, config);
      requestSeatSync(item.userIds, config);
    } catch (error) {
      if (utilitiesErrorCode(error) === SUBSCRIPTION_NOT_LIVE) waiting.push(item);
    }
  }
  writePendingSeats(waiting);
  return waiting.length;
};

/** Settles remembered seats now and every few seconds while the payment webhook catches up. */
export const usePendingSeatSettlement = () => {
  const { configurationSettings } = useConfig();
  const queryClient = useQueryClient();
  const [waiting, setWaiting] = useState(() => readPendingSeats().length);
  const [isSettling, setIsSettling] = useState(() => readPendingSeats().length > 0);

  useEffect(() => {
    if (!configurationSettings || readPendingSeats().length === 0) return;
    let stopped = false;
    const startedAt = Date.now();
    const run = async () => {
      const left = await settlePendingSeats(configurationSettings);
      if (stopped) return;
      setWaiting(left);
      // Wait for the fresh seats before saying settling is over, so no-plan screens don't flash.
      await queryClient.invalidateQueries({ queryKey: ['seat-pools'] });
      if (stopped) return;
      if (left > 0 && Date.now() - startedAt < GIVE_UP_AFTER_MS) {
        setTimeout(run, RETRY_MS);
        return;
      }
      setIsSettling(false);
    };
    run();
    return () => {
      stopped = true;
    };
  }, [configurationSettings, queryClient]);

  return { waiting, isSettling };
};
